import React, { useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import AssetWrapper from 'src/app/atoms/Asset/AssetWrapper';
import Grid from 'src/app/atoms/Layout/Grid/Grid';
import FaceWink from 'src/app/icons/Monospaced/FaceWink/FaceWink';
import HeartHand from 'src/app/icons/Monospaced/HeartHand/HeartHand';
import HeadingAndSupportingText from 'src/app/molecules/HeadingAndSupportingText/HeadingAndSupportingText';
import IconCard from 'src/app/molecules/IconCard/IconCard';
import { IContentfulImage, IContentfulVideo, IStaticMixedQuery } from 'src/types/contentful';
import useLazyLoadedURL from 'src/hooks/useLazyLoadedURL';

const FeaturesCallPeopleMagic = (): JSX.Element => {
  const { video, posterImage } = useStaticQuery<IStaticMixedQuery<IContentfulImage, IContentfulVideo>>(graphql`
    {
      video: contentfulAsset(contentful_id: { eq: "2kigaXrOS3TtxcSYa3E0eN" }) {
        file {
          url
        }
      }
      posterImage: contentfulAsset(contentful_id: { eq: "4N5OXhO3uqmL5GCbJabW20" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1440)
      }
    }
  `);

  const containerRef = useRef<HTMLDivElement>(null);

  const videoSource = useLazyLoadedURL(containerRef, video.file.url, 0.1);

  return (
    <Stack
      gap='spacing-7xl'
      direction='column'
      ref={containerRef}
    >
      <Stack
        gap='spacing-9xl'
        gapXl='spacing-5xl'
        direction='row'
        directionLg='column'
      >
        <Grid>
          <Stack
            direction='column'
            align='center'
          >
            <HeadingAndSupportingText
              heading={{
                text: 'People magic is powerful',
                size: 'lg',
                type: 'heading',
                color: 'text-primary',
              }}
              subHeading={{
                text: 'The oldest hot new thing in the world',
                size: 'md',
                type: 'paragraph',
                color: 'text-brand-secondary',
                fontWeight: 'font-weight-semibold',
              }}
              supportingText={{
                text: 'We help your members meet and connect. The concept is ancient, but the technology is advanced. The results speak for themselves.',
                size: 'xl',
                type: 'paragraph',
                color: 'text-tertiary',
              }}
              subHeadingGap='spacing-lg'
            />
          </Stack>
        </Grid>
        <Grid>
          <Stack
            direction='column'
            gap='spacing-6xl'
          >
            <IconCard
              heading={{
                text: 'Profiles that look so good we should be a dating app',
                size: 'lg',
                type: 'paragraph',
                color: 'text-primary',
                fontWeight: 'font-weight-semibold',
              }}
              supportingText={{
                text: 'Your members bring their wins, struggles, experiences and goals. We help you spotlight and celebrate it all. ',
                size: 'md',
                type: 'paragraph',
                color: 'text-tertiary',
              }}
              featuredIcon={{
                icon: <FaceWink />,
              }}
            />
            <IconCard
              heading={{
                text: 'Introductions and icebreakers without awkwardness',
                size: 'lg',
                type: 'paragraph',
                color: 'text-primary',
                fontWeight: 'font-weight-semibold',
              }}
              supportingText={{
                text: 'Every member sees the community through their own unique lens—who’s like me? Who should I meet? How can I break the ice?',
                size: 'md',
                type: 'paragraph',
                color: 'text-tertiary',
              }}
              featuredIcon={{
                icon: <HeartHand />,
              }}
            />
          </Stack>
        </Grid>
      </Stack>
      <Stack>
        <AssetWrapper
          videoProps={{
            src: videoSource,
            poster: posterImage.gatsbyImageData.images.fallback.src,
            autoPlay: true,
            muted: true,
            loop: true,
            playsInline: true,
            controls: false,
          }}
          cover
          radius='radius-2xl'
          maxWidth={1216}
        />
      </Stack>
    </Stack>
  );
};

export default FeaturesCallPeopleMagic;
